
import './synchro-modal.scss'

import { merge, Observable } from 'rxjs'
import { DateTime } from 'luxon'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faCogs, faBoxOpen, faCheck, faCloudDownloadAlt, faCloudUploadAlt, faEdit, faExclamationTriangle, faInfoCircle, faPlusCircle, faSlash, faSyncAlt, faTabletAlt, faTimes, faToggleOff, faToggleOn, faWifi } from '@fortawesome/free-solid-svg-icons'

import { DataSynchronisationStatus } from '@skinsoft/types'
import { formatDateFromNow, formatDuration } from '@skinsoft/utils'
import { DataSynchronizerStats, OfflineDataFetcher, OfflineDataUpdateCacher } from '@skinsoft/offline'
import { DataStore, dataSynchroEventBus$ } from '@skinsoft/data-store'
import { 
    AliasDataStore, 
    AuditCampaignDataStore, 
    AuthorityDataStore, 
    ConditionReportDataStore, 
    ExpressionDataStore, 
    InstitutionDataStore, 
    ItemDataStore, 
    LocationDataStore, 
    ManifestationDataStore, 
    RecordDataStore, 
    ThesaurusDataStore, 
    ThesaurusTermDataStore, 
    WorkDataStore, 
    EntityFieldDataStore 
} from '@skinsoft/entity'
import { ImageTransferHelper } from '@skinsoft/media-transfer-helper'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { OnlineStatusIndicator } from '../online-status-indicator'
import { SynchroView } from '../synchro-view'

declare const SYNCHRO_MODAL_DATASTORES: Array<{ title: string, dataStore: string, useForFetching: boolean, pushNewEntities: boolean }>;

@Component({
    components: {
        sOnlineStatusIndicator: OnlineStatusIndicator,
        sSynchroView: SynchroView
    },
    props: [''],
    template: require('./synchro-modal.html')
})
export class SynchroModal extends ReactiveVue {

    public $refs: {
        modal// Modal bootstrap (for some reason we cannot import type, maybe missing @types for bootstrap in package.json)
    }

    // Synchro
    private nbSynchroCompleted: number = 0;
    private errorInGlobalSynchro: boolean = false;
    private progressBarVariant: string = "success";
    private globalSyncing: boolean = false;
    private executionStartTime: DateTime | null = null;
    private lastSynchroStats: DataSynchronizerStats = null as any;
    private showDownloadDetails: boolean = false;

    // Push
    private pushResults: any[] = [];
    private nbEntitiesInUpdateCache: number = 0;
    private pushAllStatus: DataSynchronisationStatus = DataSynchronisationStatus.SUCCESS;
    private nbPushesCompleted: number = 0;
    private nbSuccesfullPushes: number = 0;
    private lastPushStats: any = { date: 0, status: DataSynchronisationStatus.SUCCESS };
    private globalPushing: boolean = false;
    private showUploadDetails: boolean = false;
    private pushProgressBarVariant = "success";

    showClearCacheDownDialog = false
    showClearCacheUpDialog = false

    // Icons
    private syncIcon = faSyncAlt;
    private crossIcon = faTimes;
    private wifiIcon = faWifi;
    private slashIcon = faSlash;
    private downloadIcon = faCloudDownloadAlt;
    private uploadIcon = faCloudUploadAlt;
    private infoIcon = faInfoCircle;
    private warningIcon = faExclamationTriangle;
    private localIcon = faTabletAlt;
    private switchOnIcon = faToggleOn;
    private switchOffIcon = faToggleOff;
    private checkIcon = faCheck;
    private statusErrorIcon = faTimes;
    private emptyIcon = faBoxOpen;
    private editIcon = faEdit;
    private plusIcon = faPlusCircle;
    private menuIcon = faCogs

    private $imageTransferHelper: ImageTransferHelper

    // TODO externalize for future offline apps
    private dataStores: Array<{ title: string, dataStore: DataStore<any>, useForFetching: boolean, pushNewEntities: boolean }> = []

    /*
     * sets this.dataStores with the configuration defined in vue.config.js
    */
    private setupDataStores() {
        if(SYNCHRO_MODAL_DATASTORES) {
            let dataStores: Array<{ title: string, dataStore: DataStore<any>, useForFetching: boolean, pushNewEntities: boolean } | null>;
            dataStores = SYNCHRO_MODAL_DATASTORES.map(config => {
                // @ts-ignore
                let dataStore: DataStore

                switch(config.dataStore) {
                    case "ConditionReportDataStore":
                        dataStore = ConditionReportDataStore.getInstance();
                        break;
                    case "ItemDataStore":
                        dataStore = ItemDataStore.getInstance();
                        break;
                    case "RecordDataStore":
                        dataStore = RecordDataStore.getInstance();
                        break;
                    case "AuditCampaignDataStore":
                        dataStore = AuditCampaignDataStore.getInstance();
                        break;
                    case "ThesaurusTermDataStore":
                        dataStore = ThesaurusTermDataStore.getInstance();
                        break;
                    case "ThesaurusDataStore":
                        dataStore = ThesaurusDataStore.getInstance();
                        break;
                    case "EntityFieldDataStore":
                        dataStore = EntityFieldDataStore.getInstance();
                        break;
                    case "AliasDataStore":
                        dataStore = AliasDataStore.getInstance();
                        break;
                    case "AuthorityDataStore":
                        dataStore = AuthorityDataStore.getInstance();
                        break;
                    case "LocationDataStore":
                        dataStore = LocationDataStore.getInstance();
                        break;
                    case "ManifestationDataStore":
                        dataStore = ManifestationDataStore.getInstance();
                        break;
                    case "ExpressionDataStore":
                        dataStore = ExpressionDataStore.getInstance();
                        break;
                    case "WorkDataStore":
                        dataStore = WorkDataStore.getInstance();
                        break;
                    default: 
                        return null;
                }

                return { title: config.title, dataStore, useForFetching: config.useForFetching, pushNewEntities: config.pushNewEntities }
            })

            this.dataStores = dataStores.filter(dataStoreConfig => dataStoreConfig) as Array<{ title: string, dataStore: DataStore<any>, useForFetching: boolean, pushNewEntities: boolean }>;
        }
    }

    private get pushDataStores() {
        return this.dataStores.filter(ds => ds.useForFetching);
    }

    public mounted() {
        this.setupDataStores()
        this.fetchLastSynchroStats()
        this.getNbEntitiesInUpdateCache()
        dataSynchroEventBus$.subscribe(
            async event => {
                if (event.status === DataSynchronisationStatus.SUCCESS) {
                    this.nbSynchroCompleted++;
                    // All sychros are completed
                    if (this.nbSynchroCompleted === this.nbSubSynchros) {
                        await this.saveGlobalStats(DataSynchronisationStatus.SUCCESS)
                        this.globalSyncing = false;
                    }
                } else if (event.status === DataSynchronisationStatus.ERROR) {
                    this.errorInGlobalSynchro = true;
                    this.progressBarVariant = "danger";
                    await this.saveGlobalStats(event.status)
                    this.globalSyncing = false;
                }
            }
        )
    }

    public open() {
        this.$refs.modal.show()
        this.getNbEntitiesInUpdateCache()
        // Need to refresh time-based properties
        this.$forceUpdate();
    }

    // Fetching data
    public syncAll() {
        this.executionStartTime = DateTime.now();
        this.globalSyncing = true;
        this.nbSynchroCompleted = 0;
        this.progressBarVariant = "success";
        this.errorInGlobalSynchro = false;
        for (const dataStore of this.pushDataStores) {
            dataStore.dataStore.synchronize(0, true, true)
        }
    }

    public async abortAll() {
        await this.saveGlobalStats(DataSynchronisationStatus.ABORTED)
        this.progressBarVariant = "danger";
        for (const dataStore of this.pushDataStores) {
            dataStore.dataStore.abortSynchronisation()
        }
        this.globalSyncing = false;
    }

    private async saveGlobalStats(status: DataSynchronisationStatus) {
        // Saving global sync stats to cache
        await OfflineDataFetcher.saveGlobalSyncStats({
            currentNbSychronized: 0,
            lastSynchroDate: DateTime.now().toMillis(),
            lastSynchroDuration: this.executionStartTime ? DateTime.now().diff(this.executionStartTime).toMillis() : 0,
            lastSynchroStatus: status,
            maxNbToSynchronize: 0,
        })
        this.fetchLastSynchroStats()
    }

    /**
     * Retrieve last synchro and push stats from cache
     */
    private async fetchLastSynchroStats() {
        this.lastSynchroStats = await OfflineDataFetcher.getLastGlobalSyncStats()
        this.lastPushStats = await OfflineDataUpdateCacher.getLastPushStats();
    }

    private get lastSynchroDuration() {
        return formatDuration(
            this.lastSynchroStats?.lastSynchroDuration,
            this.$t("time.minutes"),
            this.$t("time.seconds")
        ) || "..."
    }

    private get lastSynchroDate() {
        const formattedDate = formatDateFromNow(this.lastSynchroStats?.lastSynchroDate, this.$i18n.locale)
        return formattedDate !== "" ? formattedDate || "..." : "...";
    }

    private get lastSynchroStatus() {
        return this.lastSynchroStats?.lastSynchroStatus ?
            this.$t(`state.${this.lastSynchroStats.lastSynchroStatus}`) :
            "...";
    }

    private get lastPushStatus() {
        return this.lastPushStats?.status ?
            this.$t(`state.${this.lastPushStats.status}`) :
            "...";
    }


    private get formattedLastPushDate() {
        const formattedDate = formatDateFromNow(this.lastPushStats?.date, this.$i18n.locale)
        return formattedDate !== "" ? formattedDate : "...";
    }

    private statusIcon(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return faCheck;
            } else {
                return faTimes;
            }
        }
        return null;
    }

    private statusIndicatorStyle(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return "status-success";
            } else {
                return "status-error";
            }
        }
        return "";
    }

    private get nbSubSynchros() {
        return this.pushDataStores.length
    }


    private async getNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = 0;
        for (const dataStore of this.dataStores.filter(dataStore => dataStore.pushNewEntities)) {
            await dataStore.dataStore.fetchNbEntitiesInUpdateCache();
            this.nbEntitiesInUpdateCache += dataStore.dataStore.getNbEntitiesInUpdateCache
        }
    }

    private get nbDataPushers() {
        return this.dataStores.length;
    }

    // Uploading data
    private async pushAll() {
        this.nbSuccesfullPushes = 0;
        this.nbPushesCompleted = 0;
        let totalNbInCache = 0;

        this.globalPushing = true;
        this.pushResults = [];
        const pushObservables: Array<Observable<boolean>> = [];
        // Creating an array of observables, one for each entity to push
        for (const dataStore of this.dataStores.filter(dataStore => dataStore.pushNewEntities)) {
            totalNbInCache += await dataStore.dataStore.getNbCachedObjects()
            pushObservables.push(await dataStore.dataStore.handleSynchroUpdateFromCache(dataStore.pushNewEntities));
        }

        // Subscribe to all observables and figure out global push status
        merge(...pushObservables)
            .subscribe(async updateSuccess => {
                this.nbPushesCompleted++;
                if (updateSuccess) {
                    this.nbSuccesfullPushes++;
                }

                // All updates have been performed (with success or not)
                if (this.nbPushesCompleted === totalNbInCache) {
                    if (this.nbPushesCompleted !== this.nbSuccesfullPushes) {
                        this.pushAllStatus = DataSynchronisationStatus.ERROR;
                        this.pushProgressBarVariant = "danger";
                    } else {
                        this.pushAllStatus = DataSynchronisationStatus.SUCCESS;
                        this.pushProgressBarVariant = "sucess";
                    }
                    this.getNbEntitiesInUpdateCache()
                    // Saving push stats
                    const now = DateTime.now().toMillis()
                    await OfflineDataUpdateCacher.setLastPushStats(now, this.pushAllStatus);
                    this.fetchLastSynchroStats()
                    this.globalPushing = false;
                }
            })
    }

    private abortPushAll() {
        this.globalPushing = false;
        for (const dataStore of this.dataStores) {
            dataStore.dataStore.abortPush();
        }
    }

    private async onClearImageCacheClicked() {
        await this.$imageTransferHelper.cleanCachedImages()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cachedImagedCleaned'),
            variant: "info"
        });
    }

    private toggleClearCacheDownDialog() {
        this.showClearCacheDownDialog = !this.showClearCacheDownDialog
    }

    private toggleClearCacheUpDialog() {
        this.showClearCacheUpDialog = !this.showClearCacheUpDialog
    }

    private async clearCacheDown() {
        for (const dataStore of this.pushDataStores) {
            await dataStore.dataStore.clearCacheDown()
        }
        await InstitutionDataStore.getInstance().clearCacheDown()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cacheDownCleaned'),
            variant: 'warning',
        })
    }

    private async clearCacheUp() {
        await OfflineDataUpdateCacher.clear()
        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
            message: this.$t('synchroModal.cacheUpCleaned'),
            variant: 'warning',
        })
    }

    @Watch("globalSyncing")
    private onGlobalSyncingChange() {
        if (!this.globalSyncing) {
            this.$emit("sync-finished");
        }
    }

    @Watch("globalPushing")
    private onGlobalPushingChange() {
        if (!this.globalPushing) {
            this.$emit("sync-finished");
            this.emitOnBus('push-synchro-performed')
        }
    }
}